import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './authContext';

const BusinessGalleryContext = createContext();

export const useBusinessGallery = () => {
  const context = useContext(BusinessGalleryContext);
  if (!context) {
    throw new Error('useBusinessGallery must be used within a BusinessGalleryProvider');
  }
  return context;
};

export const BusinessGalleryProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for gallery endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getGalleryBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-gallery/api/v1/gallery`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-gallery/api/v1/gallery`;
    } else {
      throw new Error('Invalid login type for gallery access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business gallery');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // GET REQUESTS (READ)
  // ------------------------------------------------------------------

  const getGalleryImages = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);

      const baseUrl = getGalleryBaseUrl();
      const url = queryParams.toString()
        ? `${baseUrl}/${encodeURIComponent(registryNumber)}/images?${queryParams.toString()}`
        : `${baseUrl}/${encodeURIComponent(registryNumber)}/images`;

      const response = await makeAuthenticatedRequest(url);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch gallery images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const getImageById = useCallback(async (imageId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/image/${imageId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const getGalleryStats = useCallback(async (registryNumber) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(registryNumber)}/stats`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get gallery statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  // ------------------------------------------------------------------
  // POST REQUESTS (CREATE)
  // ------------------------------------------------------------------

  const uploadGalleryImage = useCallback(async (registryNumber, imageFile) => {
    validateAccess();
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(imageFile.type)) {
        throw new Error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (imageFile.size > maxSize) {
        throw new Error('File size exceeds 10MB limit');
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(registryNumber)}/images`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': undefined 
          }
        }
      );

      setUploadProgress(100);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to upload image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const uploadMultipleImages = useCallback(async (registryNumber, imageFiles) => {
    validateAccess();
    setLoading(true);
    setError(null);

    const results = {
      successful: [],
      failed: []
    };

    try {
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setUploadProgress(Math.round(((i) / imageFiles.length) * 100));

        try {
          const result = await uploadGalleryImage(registryNumber, file);
          results.successful.push({
            filename: file.name,
            result
          });
        } catch (err) {
          results.failed.push({
            filename: file.name,
            error: err.message
          });
        }
      }

      setUploadProgress(100);
      return results;
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  }, [uploadGalleryImage, validateAccess]);

  // ------------------------------------------------------------------
  // UPDATE REQUESTS
  // ------------------------------------------------------------------

  const updateImageOrder = useCallback(async (imageId, newOrder) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (newOrder < 1) {
        throw new Error('Image order must be at least 1');
      }

      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/image/${imageId}/order`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ image_order: newOrder })
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update image order';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const reorderImages = useCallback(async (registryNumber, imageOrders) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!Array.isArray(imageOrders) || imageOrders.length === 0) {
        throw new Error('No images provided for reordering');
      }

      for (const item of imageOrders) {
        if (!item.image_id || item.image_order < 1) {
          throw new Error('Invalid image order data');
        }
      }

      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(registryNumber)}/reorder`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ images: imageOrders })
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reorder images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const normalizeImageOrder = useCallback(async (registryNumber) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(registryNumber)}/normalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to normalize image order';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const updateImageMetadata = useCallback(async (imageId, metadata) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/image/${imageId}/metadata`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ metadata })
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update image metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  // ------------------------------------------------------------------
  // DELETE REQUESTS
  // ------------------------------------------------------------------

  const deleteImage = useCallback(async (imageId, deleteFromCloudflare = true) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      const url = deleteFromCloudflare
        ? `${baseUrl}/image/${imageId}`
        : `${baseUrl}/image/${imageId}?delete_from_cloudflare=false`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE'
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  const deleteAllImages = useCallback(async (registryNumber, deleteFromCloudflare = true) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGalleryBaseUrl();
      let url = `${baseUrl}/${encodeURIComponent(registryNumber)}/images?confirm=DELETE_ALL`;

      if (!deleteFromCloudflare) {
        url += '&delete_from_cloudflare=false';
      }

      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE'
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete all images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGalleryBaseUrl]);

  // ------------------------------------------------------------------
  // UTILITIES
  // ------------------------------------------------------------------

  const moveImageToPosition = useCallback(async (registryNumber, imageId, newPosition) => {
    validateAccess();

    try {
      const { images } = await getGalleryImages(registryNumber);

      if (!images || images.length === 0) {
        throw new Error('No images found');
      }

      const imageIndex = images.findIndex(img => img.id === imageId);
      if (imageIndex === -1) {
        throw new Error('Image not found');
      }

      const reorderedImages = [...images];
      const [movedImage] = reorderedImages.splice(imageIndex, 1);
      reorderedImages.splice(newPosition - 1, 0, movedImage);

      const orderUpdates = reorderedImages.map((img, index) => ({
        image_id: img.id,
        image_order: index + 1
      }));

      return await reorderImages(registryNumber, orderUpdates);
    } catch (err) {
      const errorMessage = err.message || 'Failed to move image';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getGalleryImages, reorderImages, validateAccess]);

  const swapImagePositions = useCallback(async (registryNumber, imageId1, imageId2) => {
    validateAccess();

    try {
      const { images } = await getGalleryImages(registryNumber);

      const image1 = images.find(img => img.id === imageId1);
      const image2 = images.find(img => img.id === imageId2);

      if (!image1 || !image2) {
        throw new Error('One or both images not found');
      }

      const orderUpdates = [
        { image_id: imageId1, image_order: image2.image_order || images.indexOf(image2) + 1 },
        { image_id: imageId2, image_order: image1.image_order || images.indexOf(image1) + 1 }
      ];

      return await reorderImages(registryNumber, orderUpdates);
    } catch (err) {
      const errorMessage = err.message || 'Failed to swap image positions';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getGalleryImages, reorderImages, validateAccess]);

  const setAsPrimaryImage = useCallback(async (registryNumber, imageId) => {
    return await moveImageToPosition(registryNumber, imageId, 1);
  }, [moveImageToPosition]);

  const getPrimaryImage = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const { images } = await getGalleryImages(registryNumber, { limit: 1 });
      return images && images.length > 0 ? images[0] : null;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get primary image';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getGalleryImages, validateAccess]);

  const hasGalleryImages = useCallback(async (registryNumber) => {
    validateAccess();
    try {
      const stats = await getGalleryStats(registryNumber);
      return stats.stats.total_images > 0;
    } catch (err) {
      return false;
    }
  }, [getGalleryStats, validateAccess]);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: getGalleryBaseUrl(),
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, getGalleryBaseUrl]);

  // ------------------------------------------------------------------
  // MEMOIZED CONTEXT VALUE (Prevents Infinite Loops)
  // ------------------------------------------------------------------
  const value = useMemo(() => ({
    // State
    loading,
    error,
    uploadProgress,

    // Read Operations
    getGalleryImages,
    getImageById,
    getGalleryStats,
    getPrimaryImage,
    hasGalleryImages,

    // Create Operations
    uploadGalleryImage,
    uploadMultipleImages,

    // Update Operations
    updateImageOrder,
    reorderImages,
    normalizeImageOrder,
    updateImageMetadata,

    // Delete Operations
    deleteImage,
    deleteAllImages,

    // Utility Functions
    moveImageToPosition,
    swapImagePositions,
    setAsPrimaryImage,

    // Helpers
    clearError,
    formatFileSize,
    getEndpointInfo,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasGalleryAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  }), [
    loading, error, uploadProgress, loginType,
    getGalleryImages, getImageById, getGalleryStats, getPrimaryImage, hasGalleryImages,
    uploadGalleryImage, uploadMultipleImages,
    updateImageOrder, reorderImages, normalizeImageOrder, updateImageMetadata,
    deleteImage, deleteAllImages,
    moveImageToPosition, swapImagePositions, setAsPrimaryImage,
    clearError, formatFileSize, getEndpointInfo
  ]);

  return (
    <BusinessGalleryContext.Provider value={value}>
      {children}
    </BusinessGalleryContext.Provider>
  );
};

export default BusinessGalleryContext;